import { useContext, useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../../../lib/dateLocale';
import { SttSelectUser } from '../../../sporttia/selectors/SttSelectUser';
import { SttFormDate } from '../../../sporttia/forms/SttFormDate';
import { SttFormTextarea } from '../../../sporttia/forms/SttFormTextarea';
import { AppContext } from '../../../sporttia/AppContext';
import { SttDialog } from '../../../sporttia/dialogs/SttDialog';
import { SttSelectActivityRate } from '../../../sporttia/selectors/SttSelectActivityRate';
import DateBuilder from '../../../lib/DateBuilder';
import { PERMISSIONS } from '../../../lib/permissions';

type Props = {
    open: boolean;
    onClose: () => void;
    activityId: number;
};

type Recovery = {
    notes: string;
    date: Date;
    idActivityRate?: number;
};

type Student = {
    id: number;
    name: string;
    surname1?: string;
    surname2?: string;
    login?: string;
};

type Assistance = {
    id: number;
    created: string;
    class: {
        id: number;
        ini: string;
        end: string;
    };
};

type AssistencesResponse = {
    count: number;
    rows: Assistance[];
};

/**
 * Dialog to recover classes.
 * @param open
 * @param onClose
 * @param activityId
 * @returns {React.ReactElement}
 */

export default function RecoverAttendanceDialog({
    open,
    onClose,
    activityId,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [recovery, setRecovery] = useState<Recovery>({
        notes: '',
        date: new Date(),
    });
    const [student, setStudent] = useState<Student | null>(null);
    const [recoverableAssistences, setRecoverableAssistences] =
        useState<AssistencesResponse>();
    const [selectedRecoverableAssistence, setSelectedRecoverableAssistence] =
        useState<Assistance>();
    const [selectedActivityRateId, setSelectedActivityRateId] =
        useState<number>();

    const loadAssistences = (): void => {
        cxt?.api('GET', `/mships/${student?.id}/assistences`, {
            params: {
                status: 'RECOVERABLE',
            },
            success: (r: AssistencesResponse) => {
                setRecoverableAssistences(r);
                setSelectedRecoverableAssistence(undefined);
            },
        });
    };

    const recoverAssistence = (): void => {
        const finalParams = recovery;

        finalParams.idActivityRate = selectedActivityRateId;

        cxt?.api(
            'POST',
            `/classes/assistences/${selectedRecoverableAssistence?.id}/recoveries`,
            {
                params: {
                    ...finalParams,
                },
                success: () => {
                    closeDialog();
                },
            },
        );
    };

    const closeDialog = (): void => {
        setStudent(null);
        setRecoverableAssistences(undefined);
        setSelectedRecoverableAssistence(undefined);
        onClose();
    };

    useEffect(() => {
        if (student && student.id) loadAssistences();
    }, [student]);
    return (
        <SttDialog
            title={`${t('Recover')}`}
            open={open}
            onClose={closeDialog}
            maxWidth="sm"
            buttons={[
                {
                    type: 'save',
                    disabled: !selectedRecoverableAssistence,
                    onClick: () => recoverAssistence(),
                    permission: PERMISSIONS.ACTIVITY.MODIFY,
                },
                {
                    type: 'close',
                    onClick: () => closeDialog(),
                },
            ]}
            content={
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                        <SttSelectUser
                            caption={t('User')}
                            name="user"
                            user={student ?? undefined}
                            onSelect={(user) =>
                                setStudent(user as Student | null)
                            }
                            returnMship
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                        <SttFormDate
                            caption={t('Date')}
                            name="date"
                            defVal={recovery.date as unknown as string}
                            onChange={({
                                name,
                                value,
                            }: {
                                name?: string;
                                value: unknown;
                            }) => {
                                if (!name) return;
                                setRecovery({ ...recovery, [name]: value });
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                        <SttFormTextarea
                            name="notes"
                            rows={3}
                            caption={t('Notes')}
                            defVal={recovery.notes}
                            onChange={({
                                name,
                                value,
                            }: {
                                name?: string;
                                value: unknown;
                            }) => {
                                if (!name) return;
                                setRecovery({
                                    ...recovery,
                                    [name]: value,
                                });
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                        <SttSelectActivityRate
                            activityId={activityId}
                            defVal={selectedActivityRateId ?? 0}
                            caption={t('Rate')}
                            onChange={(value: number) => {
                                setSelectedActivityRateId(value);
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                        {/* Aquí tengo que añadir las diferentes fechas posibles a recuperar del usuario. */}
                        <Typography variant="subtitle2" gutterBottom>
                            Recuperar{' '}
                            {selectedRecoverableAssistence && student
                                ? new DateBuilder(
                                      selectedRecoverableAssistence.created,
                                      cxt?.sc?.timezone?.name,
                                      cxt?.lang?.key,
                                  ).cdlhm()
                                : ''}
                        </Typography>

                        {student &&
                        !selectedRecoverableAssistence &&
                        recoverableAssistences &&
                        recoverableAssistences.count > 0 ? (
                            <List>
                                {recoverableAssistences.rows.map(
                                    (assistence, key) => (
                                        <ListItemButton
                                            key={key}
                                            dense
                                            onClick={() =>
                                                setSelectedRecoverableAssistence(
                                                    assistence,
                                                )
                                            }
                                        >
                                            <ListItemText
                                                id={`item-${key}`}
                                                primary={format(
                                                    parseISO(
                                                        assistence.class.ini,
                                                    ),
                                                    'EEEE, dd MMMM HH:mm',
                                                    { locale: getDateLocale() },
                                                )}
                                            />
                                        </ListItemButton>
                                    ),
                                )}
                            </List>
                        ) : !selectedRecoverableAssistence && student ? (
                            'No hay resultados'
                        ) : (
                            ''
                        )}
                    </Grid>
                </Grid>
            }
        />
    );
}
